import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
  Chip,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FiUpload, FiCheckCircle, FiAlertCircle, FiFile } from 'react-icons/fi';
import api from '../../../utils/api';

// Create a visually hidden component (equivalent to Chakra UI's VisuallyHidden)
const VisuallyHidden = styled('span')({
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: '1px',
  whiteSpace: 'nowrap'
});

/**
 * Component for importing transactions with smart categorization
 */
const ImportTransactions = () => {
  const [importData, setImportData] = useState('');
  const [importFormat, setImportFormat] = useState('json');
  const [isImporting, setIsImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  // Example data placeholders
  const jsonExample = `[
  {
    "description": "Trader Joe's Grocery",
    "amount": -85.75,
    "date": "2023-04-15T10:30:00Z"
  },
  {
    "description": "Starbucks Coffee",
    "amount": -4.95,
    "date": "2023-04-16T08:15:00Z"
  }
]`;

  const csvExample = `description,amount,date
"Trader Joe's Grocery",-85.75,2023-04-15
"Starbucks Coffee",-4.95,2023-04-16`;

  /**
   * Handle import format change
   */
  const handleFormatChange = (e) => {
    setImportFormat(e.target.value);
    setImportData('');
    setResults(null);
    setError(null);
  };

  /**
   * Parse CSV data to JSON
   */
  const parseCSV = (csvData) => {
    try {
      const lines = csvData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"(.*)"$/, '$1'));
      
      const result = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = lines[i].split(',').map(v => v.trim().replace(/^"(.*)"$/, '$1'));
        const obj = {};
        
        headers.forEach((header, index) => {
          // Handle numeric values
          if (header === 'amount') {
            obj[header] = parseFloat(values[index]);
          } 
          // Handle date values
          else if (header === 'date') {
            obj[header] = new Date(values[index]).toISOString();
          }
          // Handle string values
          else {
            obj[header] = values[index];
          }
        });
        
        result.push(obj);
      }
      
      return result;
    } catch (err) {
      throw new Error(`CSV parsing error: ${err.message}`);
    }
  };

  /**
   * Import transactions
   */
  const handleImport = async () => {
    setIsImporting(true);
    setResults(null);
    setError(null);
    
    try {
      let transactions;
      
      // Parse the input data based on the format
      if (importFormat === 'json') {
        try {
          transactions = JSON.parse(importData);
          if (!Array.isArray(transactions)) {
            throw new Error('JSON data must be an array of transactions');
          }
        } catch (err) {
          throw new Error(`Invalid JSON: ${err.message}`);
        }
      } else if (importFormat === 'csv') {
        transactions = parseCSV(importData);
      }
      
      // Validate transactions
      if (!transactions || transactions.length === 0) {
        throw new Error('No valid transactions found');
      }
      
      console.log(`Importing ${transactions.length} transactions`);
      
      // Send to API
      const response = await api.post('/api/transactions/import', { 
        transactions 
      });
      
      setResults(response.data);
      toast.success(`Successfully imported ${response.data.count} transactions`);
    } catch (err) {
      console.error('Import error:', err);
      setError(err.message || 'Error importing transactions');
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  /**
   * Show example data for the selected format
   */
  const showExample = () => {
    setImportData(importFormat === 'json' ? jsonExample : csvExample);
  };

  /**
   * Clear the form
   */
  const handleClear = () => {
    setImportData('');
    setResults(null);
    setError(null);
  };

  return (
    <Card sx={{ width: '100%', boxShadow: 2, borderRadius: 2 }}>
      <CardHeader 
        title={<Typography variant="h6">Import Transactions</Typography>}
        subheader={
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Import multiple transactions at once with smart categorization
          </Typography>
        }
      />
      
      <CardContent>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel id="import-format-label">Import Format</InputLabel>
            <Select
              labelId="import-format-label"
              value={importFormat}
              onChange={handleFormatChange}
              disabled={isImporting}
              label="Import Format"
            >
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InputLabel htmlFor="import-data" shrink>Data</InputLabel>
              <Button
                size="small"
                variant="outlined"
                onClick={showExample}
                disabled={isImporting}
                sx={{ ml: 1, mt: 1 }}
              >
                Show Example
              </Button>
            </Box>
            <TextField
              id="import-data"
              multiline
              rows={10}
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder={`Paste your ${importFormat.toUpperCase()} data here...`}
              disabled={isImporting}
              InputProps={{
                sx: { fontFamily: 'monospace' }
              }}
              fullWidth
            />
          </FormControl>
          
          {error && (
            <Box sx={{ p: 2, bgcolor: '#FDEDED', color: '#5F2120', borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FiAlertCircle />
                <Typography fontWeight="medium">{error}</Typography>
              </Stack>
            </Box>
          )}
          
          {results && (
            <Box sx={{ p: 2, bgcolor: '#EDF7ED', color: '#1E4620', borderRadius: 1 }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FiCheckCircle />
                  <Typography fontWeight="medium">
                    Successfully imported {results.count} transactions
                  </Typography>
                </Stack>
                
                <Box>
                  <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                    Categorization:
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(results.categorized / results.count) * 100} 
                    sx={{ mb: 1, borderRadius: 4 }}
                    color="success"
                  />
                  <Typography variant="body2">
                    {results.categorized} of {results.count} transactions categorized 
                    ({Math.round((results.categorized / results.count) * 100)}%)
                  </Typography>
                </Box>
                
                {results.categories && (
                  <Box>
                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                      Categorization Methods:
                    </Typography>
                    <Stack direction="row" sx={{ flexWrap: 'wrap' }} spacing={1}>
                      {Object.entries(results.categories).map(([method, count]) => (
                        <Chip 
                          key={method} 
                          label={`${method}: ${count}`}
                          color={
                            method === 'rule' ? 'success' : 
                            method === 'ml' ? 'primary' : 
                            method === 'pattern' ? 'secondary' : 'default'
                          }
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
        <Button 
          onClick={handleClear} 
          variant="outlined"
          disabled={isImporting || (!importData && !results)}
        >
          Clear
        </Button>
        <Button
          startIcon={<FiUpload />}
          variant="contained"
          color="primary"
          onClick={handleImport}
          disabled={!importData || isImporting}
        >
          {isImporting ? 'Importing...' : 'Import'}
        </Button>
      </CardActions>
      
      {isImporting && <VisuallyHidden>Importing transactions...</VisuallyHidden>}
    </Card>
  );
};

export default ImportTransactions; 